import React, { useEffect } from 'react'
import { Dropdown } from '../../ui/dropdown/Dropdown'
import { DropdownItem } from '../../ui/dropdown/DropdownItem'
import { Bot, ChevronLeft, ChevronRight, EllipsisVertical, Kanban, PencilIcon, Plus, Trash } from 'lucide-react'
import { Column } from './Kanban'
import { useBusiness } from '../../../context/BusinessContext'
import { api } from '../../../configs/api'
import toast from 'react-hot-toast'

interface KanbanOptionsProps {
    column: Column
    handleCreateTask: () => void
    handleCreateStep: () => void
    handleEditStep: () => void
    handleDeleteStep: () => void
}

interface PositionProps {
    position: 'unique' | 'first' | 'last' | 'middle'
    setPosition: (position: string) => void
}

const KanbanOptions = ({
    column,
    handleCreateTask,
    handleCreateStep,
    handleEditStep,
    handleDeleteStep
}: KanbanOptionsProps) => {
    const [isOpen, setIsOpen] = React.useState(false)
    const { business, setCurrentStep, currentTab, startLoading, stopLoading, setBusiness, setFilteredBusiness } =
        useBusiness()
    const [position, setPosition] = React.useState<PositionProps['position']>('middle')

    const toggleDropdown = () => {
        setIsOpen(!isOpen)
    }

    const closeDropdown = () => {
        setIsOpen(false)
    }

    const moveStep = async (direction: 'left' | 'right') => {
        if (!column.id) return

        const pipelineID = business.pipelines.find((pipeline: any) => pipeline.value === currentTab)?.id
        if (!pipelineID) return

        try {
            startLoading()

            //* Atualiza DB
            const response = await fetch(`${api}business/moveStep`, {
                method: 'POST',
                body: JSON.stringify({
                    pipelineID,
                    stepID: column.id,
                    direction
                })
            })

            const result = await response.json()
            if (!response.ok || !result.success) {
                toast.error(result.message || 'Erro ao mover a fase!')
                return
            }

            //? Atualiza contexto no frontend
            const updatedBusiness = business.pipelines.map((pipeline: any) => {
                if (pipeline.value === currentTab) {
                    const steps = [...pipeline.steps] // Clona para evitar mutação direta
                    const stepIndex = steps.findIndex((step: any) => step.id == column.id)

                    if (
                        stepIndex !== -1 &&
                        ((direction === 'right' && stepIndex + 1 < steps.length) ||
                            (direction === 'left' && stepIndex - 1 >= 0))
                    ) {
                        // Step atual e step de destino (direita ou esquerda)
                        const targetIndex = direction === 'right' ? stepIndex + 1 : stepIndex - 1
                        const currentStep = steps[stepIndex]
                        const targetStep = steps[targetIndex]

                        // Troca os valores de 'order' entre os dois steps
                        ;[currentStep.order, targetStep.order] = [targetStep.order, currentStep.order]

                        // Troca os steps no array
                        ;[steps[stepIndex], steps[targetIndex]] = [targetStep, currentStep]
                    }

                    return { ...pipeline, steps }
                }
                return pipeline
            })

            setBusiness({ pipelines: updatedBusiness })
            setFilteredBusiness({ pipelines: updatedBusiness })

            toast.success(result.message || 'Fase movida com sucesso!')
        } catch (error) {
            console.log(error)
        } finally {
            stopLoading()
        }
    }

    useEffect(() => {
        //? Verifica se a fase atual é a primeira ou a última pra bloquear opções de movimentação
        if (business) {
            const totalSteps = business.pipelines.find(pipeline => pipeline.value === currentTab)?.steps.length
            if (totalSteps && totalSteps > 0) {
                const firstStep = business.pipelines.find(pipeline => pipeline.value === currentTab)?.steps[0]
                const lastStep = business.pipelines.find(pipeline => pipeline.value === currentTab)?.steps[
                    totalSteps - 1
                ]
                setPosition(
                    totalSteps === 1
                        ? 'unique'
                        : firstStep?.id === column.id
                        ? 'first'
                        : lastStep?.id === column.id
                        ? 'last'
                        : 'middle'
                )
            }
        }
    }, [isOpen])

    return (
        <div className='relative'>
            <div
                className='cursor-pointer p-2 opacity-50 bg-white hover:bg-gray-200 dark:bg-white/[0.03] dark:hover:bg-white/[0.05] rounded-full'
                onClick={toggleDropdown}
            >
                <EllipsisVertical className='w-4 h-4' />
            </div>

            <Dropdown
                isOpen={isOpen}
                onClose={closeDropdown}
                className='absolute right-0 mt-[17px] flex w-[260px] flex-col rounded-2xl border border-gray-200 bg-white p-3 shadow-theme-lg dark:border-gray-800 dark:bg-gray-dark z-50'
            >
                <ul className='flex flex-col gap-1 pt-4 pb-3 '>
                    <li>
                        <DropdownItem
                            onItemClick={closeDropdown}
                            tag='a'
                            onClick={handleCreateTask}
                            className='flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300'
                        >
                            <Plus className='w-4 h-4' />
                            Novo negócio
                        </DropdownItem>
                    </li>
                    <li>
                        <DropdownItem
                            onItemClick={closeDropdown}
                            tag='a'
                            onClick={handleCreateStep}
                            className='flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300'
                        >
                            <Kanban className='w-4 h-4' />
                            Nova fase
                        </DropdownItem>
                    </li>
                    <li>
                        <DropdownItem
                            onItemClick={closeDropdown}
                            tag='a'
                            onClick={handleEditStep}
                            className='flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300'
                        >
                            <PencilIcon className='w-4 h-4' />
                            Alterar fase
                        </DropdownItem>
                    </li>
                    <li>
                        <DropdownItem
                            onItemClick={closeDropdown}
                            tag='a'
                            href='/profile'
                            className='flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300'
                        >
                            <Bot className='w-4 h-4' />
                            Automatização da fase
                        </DropdownItem>
                    </li>
                    <li>
                        <DropdownItem
                            onItemClick={closeDropdown}
                            onClick={() => moveStep('left')}
                            disabled={position === 'first' || position === 'unique'}
                            className='flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300'
                        >
                            <ChevronLeft className='w-4 h-4' />
                            Mover para esquerda
                        </DropdownItem>
                    </li>
                    <li>
                        <DropdownItem
                            onItemClick={closeDropdown}
                            onClick={() => moveStep('right')}
                            disabled={position === 'last' || position === 'unique'}
                            className='flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300'
                        >
                            <ChevronRight className='w-4 h-4' />
                            Mover para direita
                        </DropdownItem>
                    </li>
                    <li>
                        <DropdownItem
                            onItemClick={closeDropdown}
                            onClick={handleDeleteStep}
                            className='px-3 py-2 font-medium rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-error-500 dark:hover:bg-white/5 dark:hover:text-error-300 light:text-error-500'
                        >
                            <div className='text-error-400 flex items-center gap-3'>
                                <Trash className='w-4 h-4' />
                                Excluir fase
                            </div>
                        </DropdownItem>
                    </li>
                </ul>
            </Dropdown>
        </div>
    )
}

export default KanbanOptions
